<script setup lang="ts">
import { useSearchResultsRandomizer } from '~/composables/useSearchResultsRandomizer'
import { useDebounce } from '@vueuse/core'
import { LucideSearch, LucideNewspaper, LucideCircleX, SlidersVertical } from 'lucide-vue-next'
import { FeaturePagination } from '~/components/feature/pagination'
import type { IResponseData, IMultiselectOption } from '~/types'
import { Input } from '~/components/ui/input'
import { Button } from '~/components/ui/button'
import { Alert, AlertTitle, AlertDescription } from '~/components/ui/alert'
import type { ICardsQueryParams } from '~/components/entity/card/item'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '~/components/ui/dialog'
import { EntityCardItem, EntityCardSkeleton } from '~/components/entity/card'
import { useCardFilters } from '~/components/widget/cards/list-meili-search/mocks'
import type { ICardSystems } from '~/components/widget/select-card-system'
import type { IOrganization } from '~/components/entity/organization/types'

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const filtersStore = useCardsFiltersStore()
const { shuffleHighlightedAfterMount } = useSearchResultsRandomizer()
const { cardBonuses, cardCurrencyType, cardFeatures, cardType } = useCardFilters()

const props = withDefaults(
  defineProps<{
    queryParams?: ICardsQueryParams
    organization?: IOrganization
  }>(),
  {
    queryParams: () => ({
      per_page: '10',
      q: ''
    }),
    organization: undefined
  }
)

const hitsPerPage = Number(route.query.per_page || props.queryParams.per_page || 10)
const currentPage = ref(Number(route.query.page) || 1)

const isUsingLocalFilters = computed(() => !!props.organization)

const localSearchQueryString = ref(String(route.query.q || ''))
const localSelectedCardType = ref<IMultiselectOption[]>([])
const localSelectedCardCurrencyType = ref<IMultiselectOption[]>([])
const localSelectedCardOption = ref<IMultiselectOption[]>([])
const localSelectedCardSystems = ref<IMultiselectOption[]>([])
const localSelectedCardBonuses = ref<IMultiselectOption[]>([])
const localSelectedCardFeatures = ref<IMultiselectOption[]>([])
const localCurrency = ref<IMultiselectOption[]>([])

const searchQueryString = computed({
  get: () =>
    isUsingLocalFilters.value ? localSearchQueryString.value : filtersStore.searchQueryString,
  set: (val) =>
    isUsingLocalFilters.value
      ? (localSearchQueryString.value = val)
      : (filtersStore.searchQueryString = val)
})

const selectedCardType = computed({
  get: () =>
    isUsingLocalFilters.value ? localSelectedCardType.value : filtersStore.selectedCardType,
  set: (val) =>
    isUsingLocalFilters.value
      ? (localSelectedCardType.value = val)
      : (filtersStore.selectedCardType = val)
})

const selectedCardCurrencyType = computed({
  get: () =>
    isUsingLocalFilters.value
      ? localSelectedCardCurrencyType.value
      : filtersStore.selectedCardCurrencyType,
  set: (val) =>
    isUsingLocalFilters.value
      ? (localSelectedCardCurrencyType.value = val)
      : (filtersStore.selectedCardCurrencyType = val)
})

const selectedCardOption = computed({
  get: () =>
    isUsingLocalFilters.value ? localSelectedCardOption.value : filtersStore.selectedCardOption,
  set: (val) =>
    isUsingLocalFilters.value
      ? (localSelectedCardOption.value = val)
      : (filtersStore.selectedCardOption = val)
})

const selectedCardSystems = computed({
  get: () =>
    isUsingLocalFilters.value ? localSelectedCardSystems.value : filtersStore.selectedCardSystems,
  set: (val) =>
    isUsingLocalFilters.value
      ? (localSelectedCardSystems.value = val)
      : (filtersStore.selectedCardSystems = val)
})

const selectedCardBonuses = computed({
  get: () =>
    isUsingLocalFilters.value ? localSelectedCardBonuses.value : filtersStore.selectedCardBonuses,
  set: (val) =>
    isUsingLocalFilters.value
      ? (localSelectedCardBonuses.value = val)
      : (filtersStore.selectedCardBonuses = val)
})

const selectedCardFeatures = computed({
  get: () =>
    isUsingLocalFilters.value ? localSelectedCardFeatures.value : filtersStore.selectedCardFeatures,
  set: (val) =>
    isUsingLocalFilters.value
      ? (localSelectedCardFeatures.value = val)
      : (filtersStore.selectedCardFeatures = val)
})

const currency = computed({
  get: () => (isUsingLocalFilters.value ? localCurrency.value : filtersStore.currency),
  set: (val) =>
    isUsingLocalFilters.value ? (localCurrency.value = val) : (filtersStore.currency = val)
})

// Initialize search string from route query before creating debounced version
if (!isUsingLocalFilters.value && route.query.q) {
  filtersStore.searchQueryString = String(route.query.q)
}

const debouncedQuery = useDebounce(searchQueryString, 500)

const { data: systemTypes } = await useAsyncData('card-systems-meili-search-widget', () =>
  $fetch<IResponseData<ICardSystems[]>>('/card_systems', {
    baseURL: config.public.baseApiUrl
  })
)

const cardSystems = computed<IMultiselectOption[]>(
  () =>
    systemTypes.value?.data.map((item) => ({ id: item.id, label: item.title, value: item.slug })) ??
    []
)

const { data: cardOptionTypes } = await useAsyncData('card-types-meili-search-widget', () =>
  $fetch<IResponseData<ICardSystems[]>>('/card_types', {
    baseURL: config.public.baseApiUrl
  })
)

const cardTypesOptions = computed<IMultiselectOption[]>(
  () =>
    cardOptionTypes.value?.data.map((item) => ({
      id: item.id,
      label: item.title,
      value: item.slug
    })) ?? []
)

const menuItems = ['KGS', 'RUB', 'USD', 'EUR', 'KZT', 'CNY']
const currencyList = useCurrencyStore().list
const currencyOptions = computed(() => [
  ...currencyList
    .filter((c) => menuItems.includes(c.code))
    .map((c) => ({
      id: c.id,
      label: c.code,
      value: c.code.toLowerCase()
    }))
])

const activeFilter = computed(() => {
  const allFilters: string[] = []

  if (selectedCardBonuses.value.length) {
    const bonusFilters = selectedCardBonuses.value.map((o) =>
      o.value === 'interest_rate' ? `${o.value} > 0` : `${o.value} = true`
    )
    allFilters.push(`(${bonusFilters.join(' OR ')})`)
  }

  if (selectedCardFeatures.value.length) {
    const featuresFilters = selectedCardFeatures.value.map((o) => `${o.value} = true`)
    allFilters.push(`(${featuresFilters.join(' OR ')})`)
  }

  if (selectedCardSystems.value.length) {
    const cardSystemsFilters = selectedCardSystems.value.map((o) => `system.id = ${o.id}`)
    allFilters.push(`(${cardSystemsFilters.join(' OR ')})`)
  }

  if (selectedCardType.value.length) {
    const typeFilters = selectedCardType.value.map((o) => `type.${o.value} = true`)
    allFilters.push(`(${typeFilters.join(' OR ')})`)
  }

  if (selectedCardCurrencyType.value.length) {
    const currencyTypeFilters = selectedCardCurrencyType.value.map(
      (o) => `currency_type = ${o.value}`
    )
    allFilters.push(`(${currencyTypeFilters.join(' OR ')})`)
  }

  if (currency.value.length) {
    const currencyFilters = currency.value.map((o) => `settings.is_${o.value} = true`)
    allFilters.push(`(${currencyFilters.join(' OR ')})`)
  }

  if (selectedCardOption.value.length) {
    allFilters.push(`types.slug = ${selectedCardOption.value[0].value}`)
  }

  return allFilters.length ? [`(${allFilters.join(' AND ')})`] : undefined
})

const isUpdatingFromRoute = ref(false)

const initializeFiltersFromRoute = () => {
  isUpdatingFromRoute.value = true

  const typeParam = Array.isArray(route.params.type) ? route.params.type[0] : route.params.type
  const systemParam = Array.isArray(route.params.system)
    ? route.params.system[0]
    : route.params.system

  if (typeParam && cardTypesOptions.value.length > 0) {
    const type = cardTypesOptions.value.find((item) => item.value === typeParam)
    if (type) {
      if (isUsingLocalFilters.value) {
        localSelectedCardOption.value = [type]
        localSelectedCardSystems.value = []
      } else {
        filtersStore.selectedCardOption = [type]
        filtersStore.selectedCardSystems = []
      }
    }
  }

  if (systemParam && cardSystems.value.length > 0) {
    const system = cardSystems.value.find((item) => item.value === systemParam)
    if (system) {
      if (isUsingLocalFilters.value) {
        localSelectedCardSystems.value = [system]
        localSelectedCardOption.value = []
      } else {
        filtersStore.selectedCardSystems = [system]
        filtersStore.selectedCardOption = []
      }
    }
  }

  if (!typeParam && !systemParam) {
    if (isUsingLocalFilters.value) {
      localSelectedCardOption.value = []
      localSelectedCardSystems.value = []
    } else {
      filtersStore.selectedCardOption = []
      filtersStore.selectedCardSystems = []
    }
  }

  nextTick(() => {
    isUpdatingFromRoute.value = false
  })
}

initializeFiltersFromRoute()

const { search: searchCards } = useMeiliSearch(`${config.public.meiliEnvironment}_cards`)

const {
  data: result,
  pending: isSearching,
  error: fetchError,
  refresh: refreshCards
} = await useAsyncData(
  `cards-search-${props.organization?.id || 'all'}`,
  async () => {
    try {
      const searchResult = await searchCards(String(searchQueryString.value), {
        hitsPerPage,
        page: currentPage.value,
        sort: ['weight:desc', 'highlight:desc', 'updated_at:desc'],
        attributesToSearchOn: [
          'title.ky',
          'title.ru',
          'description.ky',
          'description.ru',
          'organization.title.ky',
          'organization.title.ru',
          'organization.official_title.ky',
          'organization.official_title.ru',
          'organization.slug',
          'types.title.ky',
          'types.title.ru'
        ],
        attributesToHighlight: ['title'],
        ...(activeFilter.value ? { filter: activeFilter.value } : {})
      })

      return searchResult
    } catch (e) {
      console.error('MeiliSearch fetch error:', e)
      return null
    }
  },
  {
    server: true,
    lazy: true,
    default: () => null
  }
)

const hasError = computed(() => !!fetchError.value)

const orderedHits = shuffleHighlightedAfterMount(() => result.value?.hits || [])

const typedHits = computed(() =>
  orderedHits.value.map((hit) => ({
    id: hit.id,
    url: hit.url,
    title: hit._formatted?.title[locale.value] || hit.title[locale.value],
    type: hit.type,
    currency_type: hit.currency_type,
    currencies: hit.currencies,
    logo: hit.logo,
    system: hit.system,
    organization: {
      ...hit.organization,
      title: hit.organization.title?.[locale.value] || '',
      official_title: hit.organization.official_title?.[locale.value] || ''
    },
    service_fee: hit.service_fee,
    highlight: hit.highlight,
    option_bonuses: hit.option_bonuses,
    option_cashback: hit.option_cashback,
    option_loyalty_program: hit.option_loyalty_program,
    option_online_banking: hit.option_online_banking,
    option_online_payments: hit.option_online_payments,
    option_overdraft: hit.option_overdraft,
    option_salary_project: hit.option_salary_project,
    option_sms_notifications: hit.option_sms_notifications,
    option_wireless_payments: hit.option_wireless_payments,
    ratings: hit.ratings ? hit.ratings.average_rate : null
  }))
)

const updateUrl = () => {
  router.replace({
    path: route.path,
    query: {
      ...route.query,
      q: searchQueryString.value || undefined,
      page: currentPage.value > 1 ? String(currentPage.value) : undefined
    }
  })
}

const isOpen = ref(false)

// Watch for route params changes (type and system)
watch([() => route.params.type, () => route.params.system], () => {
  initializeFiltersFromRoute()
})

// Watch for search query changes with debounce
watch(debouncedQuery, () => {
  if (isOpen.value || isUpdatingFromRoute.value) return
  updateUrl()
  refreshCards()
})

// Watch for filter changes without debounce
watch(
  [
    selectedCardBonuses,
    selectedCardFeatures,
    selectedCardType,
    selectedCardCurrencyType,
    currency,
    currentPage
  ],
  () => {
    if (isOpen.value || isUpdatingFromRoute.value) return
    updateUrl()
    refreshCards()
  }
)

const changePage = (page: number) => {
  currentPage.value = page
}

const showFiltersInsideModal = computed(() => isOpen.value)

const toggle = () => {
  isOpen.value = !isOpen.value
}

const resetFilters = () => {
  if (isUsingLocalFilters.value) {
    localSelectedCardBonuses.value = []
    localSelectedCardFeatures.value = []
    localSelectedCardType.value = []
    localSelectedCardCurrencyType.value = []
    localCurrency.value = []
    localSearchQueryString.value = ''
    localSelectedCardSystems.value = []
    localSelectedCardOption.value = []
  } else {
    filtersStore.searchQueryString = ''
    filtersStore.selectedCardBonuses = []
    filtersStore.selectedCardFeatures = []
    filtersStore.selectedCardType = []
    filtersStore.selectedCardCurrencyType = []
    filtersStore.currency = []
    filtersStore.selectedCardSystems = []
    filtersStore.selectedCardOption = []
  }

  initializeFiltersFromRoute()
}

const applyFilters = () => {
  isOpen.value = false
  updateUrl()
  refreshCards()
}
</script>

<template>
  <div class="space-y-5">
    <div class="relative">
      <Input
        v-model="searchQueryString"
        :placeholder="t('common.search_organization_placeholder')"
        class="h-[52px] pl-14 pr-14"
      />
      <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />
      <Button
        v-if="searchQueryString"
        size="icon"
        variant="link"
        class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
        @click="searchQueryString = ''"
      >
        <LucideCircleX />
      </Button>
    </div>

    <div class="flex items-start justify-between gap-2">
      <div class="grid w-full grid-cols-1 gap-2 md:grid-cols-2">
        <MultiSelect
          v-model="selectedCardBonuses"
          :options="cardBonuses"
          :placeholder="t('widget.cards.list_meili_search.bonuses_placeholder')"
          class="multiselect_height_52"
        />
        <MultiSelect
          v-model="selectedCardFeatures"
          :options="cardFeatures"
          :placeholder="t('widget.cards.list_meili_search.features_placeholder')"
          class="multiselect_height_52"
        />
      </div>
      <Button
        variant="blue"
        size="icon"
        class="mt-0.5 !p-6"
        :aria-label="$t('common.all_filters')"
        @click="toggle"
      >
        <SlidersVertical class="h-6 w-6" />
      </Button>
    </div>

    <section v-if="!isSearching && !hasError && result" aria-labelledby="cards">
      <div v-if="result.hits?.length" class="CardsList">
        <Heading id="cards" tag="h2" class="sr-only">{{ $t('common.cards') }}</Heading>
        <EntityCardItem
          v-for="item in typedHits"
          :key="item.id"
          :item="item"
          :hide-organization-details="false"
        />
        <div class="mt-7 flex justify-end">
          <FeaturePagination
            :meta="{
              current_page: result.page || 1,
              total: result.totalHits || 0,
              per_page: result.hitsPerPage || 0,
              last_page: result.totalPages || 0
            }"
            :links="{
              prev:
                currentPage > 1
                  ? `?q=${searchQueryString}&page=${result.page ? result.page - 1 : 1}`
                  : null,
              next:
                currentPage < (result.totalPages || 1)
                  ? `?q=${searchQueryString}&page=${result.page ? result.page + 1 : 1}`
                  : null
            }"
            @change="changePage"
          />
        </div>
      </div>
      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ t('common.nothing_found') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else-if="isSearching">
      <EntityCardSkeleton :count="hitsPerPage" :hide-organization-details="false" />
    </section>

    <section v-else-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ t('common.error_loading_title') }}</AlertTitle>
        <AlertDescription>{{ t('common.error_loading_description') }}</AlertDescription>
      </Alert>
    </section>

    <Dialog v-model:open="isOpen">
      <DialogContent overlay-class="bg-gray-600/50" class="max-w-3xl">
        <DialogHeader
          ><DialogTitle>{{ t('common.all_filters') }}</DialogTitle></DialogHeader
        >
        <div v-if="showFiltersInsideModal" class="grid grid-cols-1 gap-2 sm:gap-5">
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <MultiSelect
              v-model="selectedCardBonuses"
              :options="cardBonuses"
              :placeholder="t('widget.cards.list_meili_search.bonuses_placeholder')"
              class="multiselect_height_52"
            />
            <MultiSelect
              v-model="selectedCardFeatures"
              :options="cardFeatures"
              :placeholder="t('widget.cards.list_meili_search.features_placeholder')"
              class="multiselect_height_52"
            />
          </div>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <MultiSelect
              v-model="selectedCardSystems"
              :options="cardSystems"
              :placeholder="t('widget.cards.list_meili_search.payment_systems_placeholder')"
              class="multiselect_height_52"
            />
            <MultiSelect
              v-model="selectedCardType"
              :options="cardType"
              :placeholder="t('widget.cards.list_meili_search.card_type_placeholder')"
              class="multiselect_height_52"
            />
          </div>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <MultiSelect
              v-model="selectedCardCurrencyType"
              :options="cardCurrencyType"
              :placeholder="t('widget.cards.list_meili_search.currency_type_placeholder')"
              class="multiselect_height_52"
            />
            <MultiSelect
              v-model="currency"
              :options="currencyOptions"
              :placeholder="t('widget.cards.list_meili_search.supported_currencies_placeholder')"
              class="multiselect_height_52"
            />
          </div>
        </div>
        <DialogFooter class="gap-2">
          <Button variant="outline-blue" @click="resetFilters">{{
            t('common.reset_filters')
          }}</Button>
          <Button variant="blue" @click="applyFilters">{{ t('common.show') }}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
